'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Settings,
  FileText,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Bell,
  Zap,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const navItems: NavItem[] = [
  {
    id: 'nav-dashboard',
    label: 'Lead Inbox',
    href: '/decision-dashboard',
    icon: <LayoutDashboard size={20} />,
    badge: 3,
  },
  {
    id: 'nav-quote',
    label: 'Quick Quote',
    href: '/quick-quote-form',
    icon: <FileText size={20} />,
  },
  {
    id: 'nav-settings',
    label: 'Business Settings',
    href: '/business-settings',
    icon: <Settings size={20} />,
  },
];

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`
        relative flex flex-col bg-[#0D0D0D] border-r border-[#1A1A1A] h-full
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-[#1A1A1A] px-4 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        {!collapsed ? (
          <AppLogo size={36} />
        ) : (
          <AppLogo size={28} />
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map((item) => {
          const isActive = currentPath === item.href || currentPath.startsWith(item.href);
          return (
            <Link
              key={item.id}
              href={item.href}
              className={`
                relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group
                ${isActive
                  ? 'bg-blue-600/15 text-blue-400 border border-blue-600/25' :'text-[#666] hover:text-white hover:bg-[#1A1A1A]'
                }
                ${collapsed ? 'justify-center' : ''}
              `}
              title={collapsed ? item.label : undefined}
            >
              <span className={`flex-shrink-0 ${isActive ? 'text-blue-400' : ''}`}>
                {item.icon}
              </span>
              {!collapsed && (
                <span className="text-sm font-500 truncate">{item.label}</span>
              )}
              {item.badge && !collapsed && (
                <span className="ml-auto bg-amber-500 text-black text-[10px] font-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {item.badge}
                </span>
              )}
              {item.badge && collapsed && (
                <span className="absolute top-1 right-1 bg-amber-500 text-black text-[9px] font-700 rounded-full w-4 h-4 flex items-center justify-center">
                  {item.badge}
                </span>
              )}
              {/* Tooltip for collapsed */}
              {collapsed && (
                <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white text-xs font-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50 shadow-modal">
                  {item.label}
                  {item.badge && <span className="ml-2 bg-amber-500 text-black text-[9px] font-700 rounded-full px-1.5 py-0.5">{item.badge}</span>}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Subscription badge */}
      {!collapsed && (
        <div className="mx-3 mb-3 p-3 bg-blue-600/10 border border-blue-600/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-amber-400" />
            <span className="text-xs font-600 text-white">Pro Plan</span>
          </div>
          <p className="text-[11px] text-[#666]">$29/mo · Renews May 20</p>
        </div>
      )}

      {/* Bottom actions */}
      <div className={`border-t border-[#1A1A1A] p-2 space-y-1`}>
        <button
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#666] hover:text-white hover:bg-[#1A1A1A] transition-all duration-150 group ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Notifications' : undefined}
        >
          <Bell size={18} />
          {!collapsed && <span className="text-sm font-500">Notifications</span>}
          {collapsed && (
            <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white text-xs font-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50 shadow-modal">
              Notifications
            </div>
          )}
        </button>

        <Link
          href="/sign-up-login-screen"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#666] hover:text-red-400 hover:bg-red-500/10 transition-all duration-150 group ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-500">Sign Out</span>}
          {collapsed && (
            <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white text-xs font-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50 shadow-modal">
              Sign Out
            </div>
          )}
        </Link>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#444] hover:text-white hover:bg-[#1A1A1A] transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="text-xs font-500 text-[#444]">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}